import * as z from 'zod';
import { CheckoutSection } from '../../constants/checkout-section.enum';
import { PaymentMethod } from '../../constants/payment-method.enum';
import { compactBankValue } from '../../lib/format';
import { isValidPhone } from '../../lib/phone';
import { AcceptOfferPayload } from '../../types/request';
import { OfferCheckoutCustomerResponseDto } from '../../types/response';

const required = (message: string) => z.string().trim().min(1, message);

// Format only – the backend checks the IBAN checksum (`@IsIBAN`) on accept.
const IBAN_PATTERN = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
const BIC_PATTERN = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

/** Each card section is validated on its own: complete sections start collapsed, incomplete ones open as inputs. */
const dataSectionSchema = z.object({
  firstName: required('Vorname ist erforderlich'),
  lastName: required('Nachname ist erforderlich'),
  name: required('Firma ist erforderlich'),
  vatId: required('USt-ID ist erforderlich'),
  street: required('Straße ist erforderlich'),
  houseNumber: required('Hausnummer ist erforderlich'),
  postCode: required('PLZ ist erforderlich'),
  city: required('Ort ist erforderlich'),
  country: required('Land ist erforderlich')
});

const contactSectionSchema = z.object({
  phone: required('Telefon ist erforderlich').refine(isValidPhone, 'Bitte gib eine gültige Telefonnummer ein, z. B. +491771234567'),
  // Optional – without it the invoices go to the customer mail.
  invoiceMail: z
    .string()
    .trim()
    .refine((value) => !value || z.string().email().safeParse(value).success, 'Bitte gib eine gültige E-Mail-Adresse ein')
});

export const sepaSectionSchema = z.object({
  iban: z.string().refine((value) => IBAN_PATTERN.test(compactBankValue(value)), 'Bitte gib eine gültige IBAN ein'),
  bic: z.string().refine((value) => BIC_PATTERN.test(compactBankValue(value)), 'Bitte gib eine gültige BIC ein')
});

export const createOfferCheckoutFormSchema = (paymentMethod: PaymentMethod) =>
  dataSectionSchema
    .merge(contactSectionSchema)
    .merge(z.object({ iban: z.string(), bic: z.string() }))
    .merge(
      z.object({
        newCustomerDate: required('Bitte wähle ein Startdatum aus'),
        termsAccepted: z.boolean().refine((value) => value, 'Bitte akzeptiere die AGB'),
        sepaMandateAccepted: z.boolean()
      })
    )
    .superRefine((values, ctx) => {
      if (paymentMethod !== PaymentMethod.SEPA) return;
      if (!values.sepaMandateAccepted) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sepaMandateAccepted'], message: 'Bitte erteile das SEPA-Lastschriftmandat' });
      }
      const result = sepaSectionSchema.safeParse(values);
      if (result.success) return;
      result.error.issues.forEach((issue) => ctx.addIssue(issue));
    });

export type OfferCheckoutFormValues = z.infer<ReturnType<typeof createOfferCheckoutFormSchema>>;

export const SECTION_FIELDS: Record<CheckoutSection, (keyof OfferCheckoutFormValues)[]> = {
  [CheckoutSection.DATA]: ['firstName', 'lastName', 'name', 'vatId', 'street', 'houseNumber', 'postCode', 'city', 'country'],
  [CheckoutSection.CONTACT]: ['phone', 'invoiceMail'],
  [CheckoutSection.PAYMENT]: ['iban', 'bic']
};

export const buildOfferCheckoutFormDefaults = (customer: OfferCheckoutCustomerResponseDto, startDateOptions: string[]): OfferCheckoutFormValues => ({
  firstName: customer.firstName ?? '',
  lastName: customer.lastName ?? '',
  name: customer.name ?? '',
  vatId: customer.vatId ?? '',
  street: customer.street ?? '',
  houseNumber: customer.houseNumber ?? '',
  postCode: customer.postCode ?? '',
  city: customer.city ?? '',
  country: customer.country ?? '',
  phone: customer.phone ?? '',
  invoiceMail: customer.invoiceMail ?? '',
  iban: '',
  bic: '',
  // A prefilled date only counts if it is still one of the selectable ones.
  newCustomerDate: customer.newCustomerDate && startDateOptions.includes(customer.newCustomerDate) ? customer.newCustomerDate : '',
  termsAccepted: false,
  sepaMandateAccepted: false
});

/** Sections the customer has to fill in – they start open. Payment only counts for SEPA (bank data is never prefilled). */
export const getIncompleteSections = (values: OfferCheckoutFormValues, paymentMethod: PaymentMethod): CheckoutSection[] => {
  const sections: CheckoutSection[] = [];
  if (!dataSectionSchema.safeParse(values).success) sections.push(CheckoutSection.DATA);
  // Without an invoice mail the section stays open until the customer confirms it – a collapsed optional field is easily overlooked.
  if (!contactSectionSchema.safeParse(values).success || !values.invoiceMail.trim()) sections.push(CheckoutSection.CONTACT);
  if (paymentMethod === PaymentMethod.SEPA && !sepaSectionSchema.safeParse(values).success) sections.push(CheckoutSection.PAYMENT);
  return sections;
};

export const toAcceptOfferRequest = (values: OfferCheckoutFormValues, paymentMethod: PaymentMethod): AcceptOfferPayload => ({
  firstName: values.firstName.trim(),
  lastName: values.lastName.trim(),
  name: values.name.trim(),
  vatId: values.vatId.trim(),
  street: values.street.trim(),
  houseNumber: values.houseNumber.trim(),
  postCode: values.postCode.trim(),
  city: values.city.trim(),
  country: values.country,
  phone: values.phone.trim(),
  ...(values.invoiceMail.trim() && { invoiceMail: values.invoiceMail.trim() }),
  newCustomerDate: values.newCustomerDate,
  ...(paymentMethod === PaymentMethod.SEPA && { iban: compactBankValue(values.iban), bic: compactBankValue(values.bic), sepaMandateAccepted: true }),
  termsAccepted: true
});
